/**
 * @description 轻量 SEO 工具（零依赖，直接操作 document.head）
 *  支持按路由设置 title / description / robots / canonical / Open Graph / Twitter Card，
 *  以及注入 JSON-LD 结构化数据。适配纯客户端渲染(SPA)的元信息级 SEO。
 */
import { onUnmounted, watchEffect } from 'vue'
import { useRoute } from 'vue-router'

export const SITE_NAME = (import.meta.env.VITE_SITE_NAME as string) || 'Prompt Gallery'
export const SITE_URL = ((import.meta.env.VITE_SITE_URL as string) || '').replace(/\/$/, '')
export const OG_IMAGE = (import.meta.env.VITE_OG_IMAGE as string) || '/og-image.png'

export const DEFAULT_DESC =
  'Prompt Gallery · 精选 AI 绘画提示词画廊，汇集海量优质 Midjourney / GPT-Image 提示词与成图，支持按分类、风格、场景筛选与关键词搜索，灵感一键获取。'

/** 拼接绝对 URL（无 SITE_URL 时回退为相对路径） */
export function absUrl(path = ''): string {
  if (!SITE_URL)
    return path
  return SITE_URL + (path.startsWith('/') ? path : `/${path}`)
}

function upsertMeta(attr: 'name' | 'property', key: string, content?: string) {
  if (content === undefined || content === null || content === '')
    return
  let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function upsertLink(rel: string, href: string) {
  if (!href)
    return
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

function upsertJsonLd(id: string, data: object | object[]) {
  let el = document.getElementById(id) as HTMLScriptElement | null
  if (!el) {
    el = document.createElement('script')
    el.type = 'application/ld+json'
    el.id = id
    document.head.appendChild(el)
  }
  el.textContent = JSON.stringify(data)
}

export interface PageSeoOptions {
  /** 页面标题（不含站点名，会自动拼接「· SITE_NAME」）。可为 getter 以实现拉取数据后动态更新。 */
  title?: string | (() => string | undefined)
  /** 页面描述。可为 getter。 */
  description?: string | (() => string | undefined)
  /** 指定路径（默认取当前路由 path），用于 canonical / og:url。可为 getter。 */
  path?: string | (() => string | undefined)
  /** Open Graph 类型，默认 website */
  ogType?: 'website' | 'article'
  /** 是否禁止索引（登录 / 上传 / 个人中心等页设为 true） */
  noindex?: boolean
  /** 社交分享图（默认站点 OG 图）。可为 getter。 */
  image?: string | (() => string | undefined)
}

/** 解析可能为 getter 的值 */
function resolve<T>(v: T | (() => T) | undefined): T | undefined {
  return typeof v === 'function' ? (v as () => T)() : v
}

/**
 * @description 在 setup 中调用，按当前路由注入完整 SEO 元信息。
 *  路由变化时自动更新；组件卸载后保留（避免空白闪烁）。
 *  title/description/path/image 支持传入 getter，实现异步数据就绪后动态刷新 SEO。
 */
export function usePageSeo(opts: PageSeoOptions = {}) {
  const route = useRoute()
  watchEffect(() => {
    const path = resolve(opts.path) ?? route.path
    const title = resolve(opts.title)
    const pageTitle = title ? `${title} · ${SITE_NAME}` : SITE_NAME
    const url = absUrl(path)
    const desc = resolve(opts.description) || DEFAULT_DESC
    const img = absUrl(resolve(opts.image) || OG_IMAGE)

    document.title = pageTitle
    upsertMeta('name', 'description', desc)
    upsertMeta('name', 'robots', opts.noindex ? 'noindex,nofollow' : 'index,follow')
    upsertMeta('property', 'og:title', pageTitle)
    upsertMeta('property', 'og:description', desc)
    upsertMeta('property', 'og:type', opts.ogType || 'website')
    upsertMeta('property', 'og:url', url)
    upsertMeta('property', 'og:site_name', SITE_NAME)
    upsertMeta('property', 'og:image', img)
    upsertMeta('property', 'og:locale', 'zh_CN')
    upsertMeta('name', 'twitter:card', 'summary_large_image')
    upsertMeta('name', 'twitter:title', pageTitle)
    upsertMeta('name', 'twitter:description', desc)
    upsertMeta('name', 'twitter:image', img)
    upsertLink('canonical', url)
  })
}

/**
 * @description 注入 JSON-LD 结构化数据。data 为工厂函数，便于依赖响应式数据（如列表）。
 *  组件卸载时自动移除该段脚本，避免跨页残留。
 */
export function useJsonLd(id: string, data: () => object | object[]) {
  watchEffect(() => {
    upsertJsonLd(id, data())
  })
  onUnmounted(() => {
    document.getElementById(id)?.remove()
  })
}
